// Layer 3 (Transformation): the Excursion investigation workflow's runs and the
// playback schedule that plays the K6 recording as if live (D24 / K15).
//
// A run is a projection of the case's analysis run plus its committed agent
// trace: the run id, the case-clock start, its status at the replay cursor and
// the step count all derive from those two sources, never from a typed table.
// The playback schedule rescales the trace's own step timestamps to a watchable
// total, so the column plays the recording at a believable pace.

import type { AgentTrace } from '@/shared/contracts/analytics/v1';
import type { Case } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { timeMilliseconds } from '@/versions/v1/shared/clock.derive';
import {
  agentToolLabel,
  investigationRows,
  type AgentTool,
} from '@/versions/v1/modules/advisory/AgentInvestigation.transform';

/** The workflow's name on every surface (case column, Diagnosis stage, catalogue). */
export const EXCURSION_WORKFLOW_ID = 'wf-excursion-investigation';
export const EXCURSION_WORKFLOW_NAME = 'Excursion investigation';
/** Trigger copy, read by the case column and the Workflows catalogue. */
export const EXCURSION_WORKFLOW_TRIGGER = 'A case reaching Diagnosis, or manual from the case';

/**
 * Watchable playback total: the recorded 67.2 s investigation replays in 30 s.
 * The scale is `PLAYBACK_TOTAL_MS ÷ trace.duration_ms` (≈ ×0.446), so every
 * step keeps its recorded order and relative spacing at a little under half speed.
 */
export const PLAYBACK_TOTAL_MS = 30_000;

/**
 * K15 round 2: the first exchange reveals almost at once, so pressing Run puts
 * the column to work instead of showing dead air. The recorded spacing of every
 * exchange after the first is untouched and the total keeps its watchable length.
 */
export const FIRST_REVEAL_MS = 300;

export type InvestigationRunStatus = 'running' | 'at-gate' | 'approved';

export type InvestigationRun = {
  /** `INV-0001`, sequential over the case's runs. */
  id: string;
  workflowId: typeof EXCURSION_WORKFLOW_ID;
  workflow: typeof EXCURSION_WORKFLOW_NAME;
  caseId: string;
  caseCode: string;
  fixture: string;
  /** The case clock when the workflow started. */
  startedAt: SeriesTime;
  /** Tool exchanges in the recorded trace. */
  steps: number;
  /** The recorded run's own duration, kept as provenance for the stated scale. */
  durationMs: number;
  /** The run's case-clock duration: the watchable playback total the column shows. */
  playbackMs: number;
  /** True for the offline K6 recording; false for a run started on the glass. */
  recorded: boolean;
};

export type InvestigationRunRow = InvestigationRun & { status: InvestigationRunStatus };

export type InvestigationPlayback = {
  /** Reveal time (ms from run start) for each tool exchange, in trace order. */
  revealsMs: readonly number[];
  /** The recommendation lands when the recorded run ends. */
  recommendationAtMs: number;
  totalMs: number;
  /** Playback total ÷ recorded duration: the scale the pacing was derived at. */
  scale: number;
};

/** `INV-0001` … — the next id after the runs already recorded for the case. */
export function nextInvestigationRunId(runs: readonly InvestigationRun[]): string {
  const highest = runs.reduce((max, run) => {
    const value = Number.parseInt(run.id.replace(/^INV-/, ''), 10);
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0);
  return `INV-${String(highest + 1).padStart(4, '0')}`;
}

/**
 * The offline recorded run (K6): the case reached Diagnosis, so the workflow
 * triggered. Its start is the case's `diagnosedAt` — the trace's own
 * `recorded_at` is the offline recording instant, after the cursor, and never
 * places a run on the case clock.
 */
export function recordedInvestigationRun(input: {
  caseId: string;
  caseCode: string;
  fixture: string;
  diagnosedAt: SeriesTime | undefined;
  trace: AgentTrace;
}): InvestigationRun | null {
  if (!input.diagnosedAt) return null;
  return {
    // The one recording is the case's first run: `INV-0001` is this run's own
    // id, not a constant any other run inherits (session runs are numbered on
    // from here by `nextInvestigationRunId`).
    id: 'INV-0001',
    workflowId: EXCURSION_WORKFLOW_ID,
    workflow: EXCURSION_WORKFLOW_NAME,
    caseId: input.caseId,
    caseCode: input.caseCode,
    fixture: input.fixture,
    startedAt: input.diagnosedAt,
    steps: investigationRows(input.trace).length,
    durationMs: input.trace.duration_ms,
    playbackMs: PLAYBACK_TOTAL_MS,
    recorded: true,
  };
}

/**
 * The case that owns a recorded trace, and that trace's recorded run. Passing
 * the trace in means every surface derives the same run from the same
 * committed recording, with no case id or fixture retyped.
 */
export function recordedRunForTrace(
  cases: readonly Case[],
  trace: AgentTrace,
): InvestigationRun | null {
  const owner = cases.find((row) => row.analysisRun?.fixture === trace.fixture);
  if (!owner) return null;
  return recordedInvestigationRun({
    caseId: owner.id,
    caseCode: owner.code ?? owner.id,
    fixture: trace.fixture,
    diagnosedAt: owner.diagnosedAt as SeriesTime | undefined,
    trace,
  });
}

/** A run started on the glass: its clock is the replay cursor (the brief's run id + start time). */
export function startedInvestigationRun(input: {
  runs: readonly InvestigationRun[];
  caseId: string;
  caseCode: string;
  fixture: string;
  cursor: SeriesTime;
  trace: AgentTrace;
}): InvestigationRun {
  return {
    id: nextInvestigationRunId(input.runs),
    workflowId: EXCURSION_WORKFLOW_ID,
    workflow: EXCURSION_WORKFLOW_NAME,
    caseId: input.caseId,
    caseCode: input.caseCode,
    fixture: input.fixture,
    startedAt: input.cursor,
    steps: investigationRows(input.trace).length,
    durationMs: input.trace.duration_ms,
    playbackMs: PLAYBACK_TOTAL_MS,
    recorded: false,
  };
}

/**
 * The workflow's trigger condition (K15 round 2): a run is offered only once
 * the case has reached Diagnosis — the replay cursor is at or past the case's
 * `diagnosedAt`, which is also the recorded run's start. Before that the
 * workflow has not fired: no run affordance, no trace and no gate. A case with
 * no diagnosis clock has not reached Diagnosis either.
 */
export function investigationRunAvailable(input: {
  diagnosedAt: SeriesTime | undefined;
  cursor: SeriesTime;
}): boolean {
  return input.diagnosedAt !== undefined
    && timeMilliseconds(input.cursor) >= timeMilliseconds(input.diagnosedAt);
}

/**
 * The playback schedule: each tool exchange is revealed when the trace's own
 * `elapsed_ms` reaches it, rescaled so the whole recording plays in
 * `totalMs`. Monotonic in trace order and bounded by `totalMs`; the first
 * exchange is pulled forward to `FIRST_REVEAL_MS` so the press shows work at once.
 */
export function investigationPlayback(
  trace: AgentTrace,
  totalMs: number = PLAYBACK_TOTAL_MS,
): InvestigationPlayback {
  const rows = investigationRows(trace);
  const recordedMs = Math.max(1, trace.duration_ms);
  const scale = totalMs / recordedMs;
  const revealsMs = rows.map((row, index) => {
    const at = Math.min(totalMs, Math.max(0, Math.round(row.elapsedMs * scale)));
    return index === 0 ? Math.min(at, FIRST_REVEAL_MS) : at;
  });
  return { revealsMs, recommendationAtMs: totalMs, totalMs, scale };
}

/** How much of the trace a run shows after `elapsedMs` of its clock. */
export function playbackProgress(
  schedule: InvestigationPlayback,
  elapsedMs: number,
): { visibleCount: number; complete: boolean } {
  const elapsed = Math.max(0, elapsedMs);
  return {
    visibleCount: schedule.revealsMs.filter((at) => at <= elapsed).length,
    complete: elapsed >= schedule.totalMs,
  };
}

/**
 * The state a run starts in. Reduced motion skips the pacing entirely: the
 * finished trace is on the page at once, and the gate follows immediately.
 */
export function runPlaybackStart(
  run: InvestigationRun,
  reducedMotion: boolean,
): { runId: string; offsetMs: number; playing: boolean } {
  return {
    runId: run.id,
    offsetMs: reducedMotion ? run.playbackMs : 0,
    playing: !reducedMotion,
  };
}

/** `11:30:04` — a step's own clock, printed on the case clock (never a duration-only label). */
export function investigationStepClock(startedAt: SeriesTime, offsetMs: number): string {
  const date = new Date(timeMilliseconds(startedAt) + Math.max(0, offsetMs));
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

/** The step clocks the column prints: each exchange at its playback reveal time, so the rows read as they land. */
export function investigationStepClocks(
  run: InvestigationRun,
  schedule: InvestigationPlayback,
): string[] {
  return schedule.revealsMs.map((at) => investigationStepClock(run.startedAt, at));
}

/** The recommendation's clock: the run's end on the same playback timeline. */
export function investigationEndClock(run: InvestigationRun): string {
  return investigationStepClock(run.startedAt, run.playbackMs);
}

/**
 * A run's status in the shared runs list: `running` while its playback is on
 * screen, `at-gate` once the replay clock has passed its start and playback has
 * reached the gate, and `approved` once an action closes it. A surface that is
 * not the column passes the run id it is currently playing, so the landing and
 * the catalogue never call a playing run `At gate` (K15 round 2).
 */
export function investigationRunStatus(
  run: InvestigationRun,
  approvedAt?: SeriesTime,
  playing = false,
): InvestigationRunStatus {
  if (approvedAt !== undefined && approvedAt >= run.startedAt) return 'approved';
  if (playing) return 'running';
  return 'at-gate';
}

export function investigationRunStatusLabel(status: InvestigationRunStatus): string {
  return status === 'approved' ? 'Approved' : status === 'at-gate' ? 'At gate' : 'Running';
}

/**
 * The runs whose clock has reached the cursor, newest first. A run started
 * after the cursor has not happened yet and never appears.
 */
export function investigationRunsAtCursor(
  runs: readonly InvestigationRun[],
  cursor: SeriesTime,
): InvestigationRun[] {
  return runs
    .filter((run) => timeMilliseconds(run.startedAt) <= timeMilliseconds(cursor))
    // Two runs started at the same cursor resolve by id, so the newest run is
    // the one the column reads at rest.
    .sort((a, b) => timeMilliseconds(b.startedAt) - timeMilliseconds(a.startedAt) || b.id.localeCompare(a.id));
}

/** The run the at-rest column reads: the newest one the replay clock has passed, or none. */
export function lastInvestigationRun(
  runs: readonly InvestigationRun[],
  cursor: SeriesTime,
): InvestigationRun | null {
  return investigationRunsAtCursor(runs, cursor)[0] ?? null;
}

/**
 * The at-rest Investigation column. While the replay clock sits before every
 * run's start the workflow has not run for this case; once the clock has passed
 * the last run's start, the column shows that recorded run finished at its
 * gate. The playback and the replay clock therefore agree: the trace is absent
 * exactly while the recorded run lies in the cursor's future, and the live
 * playback (started from the same cursor) is the only partial view.
 */
export type InvestigationColumnRead =
  | { state: 'not-run' }
  | { state: 'run'; run: InvestigationRun; status: InvestigationRunStatus };

export function investigationColumnRead(input: {
  runs: readonly InvestigationRun[];
  cursor: SeriesTime;
  approvedAt?: SeriesTime;
  /** A deep link's run (`?run=`): that run instead of the newest one. */
  runId?: string;
}): InvestigationColumnRead {
  const run = input.runId
    ? investigationRunsAtCursor(input.runs, input.cursor).find((row) => row.id === input.runId) ?? null
    : lastInvestigationRun(input.runs, input.cursor);
  if (!run) return { state: 'not-run' };
  return { state: 'run', run, status: investigationRunStatus(run, input.approvedAt) };
}

/**
 * The runs list every surface shares (the Diagnosis stage and the Workflows
 * catalogue): the case's recorded run first, then this session's runs, filtered
 * to the cursor, newest first, each with its status. Newest-first ordering and
 * id de-duplication mean a surface that derived the recorded run itself and one
 * that read it from the session still show the same list. `playingRunId` is the
 * run the case column is streaming right now, so a playing run reads `Running`
 * rather than `At gate`.
 */
export function investigationRunRows(input: {
  recorded: InvestigationRun | null;
  session?: readonly InvestigationRun[];
  cursor: SeriesTime;
  /** Case id → the session's approved action time (D19), so a run closes with its gate. */
  approvedAtByCase?: ReadonlyMap<string, SeriesTime>;
  /** The run currently playing on the case column (`Running` until its gate lands). */
  playingRunId?: string | null;
}): InvestigationRunRow[] {
  const all = [...(input.recorded ? [input.recorded] : []), ...(input.session ?? [])];
  const unique = [...new Map(all.map((run) => [run.id, run])).values()];
  return investigationRunsAtCursor(unique, input.cursor).map((run) => ({
    ...run,
    status: investigationRunStatus(
      run,
      input.approvedAtByCase?.get(run.caseId),
      run.id === input.playingRunId,
    ),
  }));
}

/**
 * The workflow's read-only tools, in the order the K6 recording first used
 * them. Read from the committed trace, never retyped: the recording script's
 * `TOOLS` set and the generated `AgentTrace.Tool` union are the same seven.
 */
export function investigationWorkflowTools(trace: AgentTrace): AgentTool[] {
  const tools: AgentTool[] = [];
  for (const row of investigationRows(trace)) {
    if (row.tool && !tools.includes(row.tool)) tools.push(row.tool);
  }
  return tools;
}

export function investigationToolList(trace: AgentTrace): string {
  return investigationWorkflowTools(trace).map((tool) => agentToolLabel(tool)).join(' · ');
}

/** `11 steps` / `1 step` — one pluralizing helper for the run counts on every surface. */
export function runStepCount(steps: number): string {
  return `${steps} step${steps === 1 ? '' : 's'}`;
}

/** `30 s` — the run's watchable case-clock duration (comp shape: `6 steps · 41 s`). */
export function formatRunDuration(ms: number): string {
  return `${Math.round(ms / 1000)} s`;
}

/** `11 steps · 30 s` — the run readout on the Investigation column header. */
export function runReadout(run: InvestigationRun): string {
  return `${runStepCount(run.steps)} · ${formatRunDuration(run.playbackMs)}`;
}

/**
 * `4 of 11 steps` — the column header readout while a run is playing or paused
 * mid-run; at rest and when finished it is `runReadout` (K15 round 2: one
 * readout, not two).
 */
export function runProgressReadout(visibleCount: number, steps: number): string {
  return `${visibleCount} of ${runStepCount(steps)}`;
}
